// Native camera + file picker helpers.
//
// Every export is safe to import on the web: the Capacitor plugins are loaded
// with dynamic imports and every entry point bails out via `isNativeApp()`.
// Each helper returns real `File` objects so existing upload code paths
// (FormData, Supabase/R2 uploads, canvas compression) keep working unchanged.

import { isNativeApp } from "./platform";

export type NativePickSource = "camera" | "photos" | "files";

const dataUrlToFile = async (dataUrl: string, name: string, type: string): Promise<File> => {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  return new File([blob], name, { type: type || blob.type || "application/octet-stream" });
};

const extForMime = (mime: string): string => {
  if (mime.includes("png")) return "png";
  if (mime.includes("webp")) return "webp";
  if (mime.includes("heic")) return "heic";
  return "jpg";
};

/** Take a single photo with the device camera. */
export async function captureCameraPhoto(): Promise<File[]> {
  if (!isNativeApp()) return [];
  const { Camera, CameraResultType, CameraSource } = await import("@capacitor/camera");
  const photo = await Camera.getPhoto({
    quality: 85,
    allowEditing: false,
    correctOrientation: true,
    resultType: CameraResultType.DataUrl,
    source: CameraSource.Camera,
  });
  if (!photo.dataUrl) return [];
  const mime = `image/${photo.format || "jpeg"}`;
  const file = await dataUrlToFile(photo.dataUrl, `photo-${Date.now()}.${extForMime(mime)}`, mime);
  return [file];
}

/** Pick one or more images from the device photo library. */
export async function pickPhotos(multiple = true): Promise<File[]> {
  if (!isNativeApp()) return [];
  const { Camera, CameraResultType, CameraSource } = await import("@capacitor/camera");

  if (!multiple) {
    const photo = await Camera.getPhoto({
      quality: 90,
      allowEditing: false,
      correctOrientation: true,
      resultType: CameraResultType.DataUrl,
      source: CameraSource.Photos,
    });
    if (!photo.dataUrl) return [];
    const mime = `image/${photo.format || "jpeg"}`;
    return [await dataUrlToFile(photo.dataUrl, `image-${Date.now()}.${extForMime(mime)}`, mime)];
  }

  const { photos } = await Camera.pickImages({ quality: 90, correctOrientation: true });
  const files: File[] = [];
  for (const [i, p] of photos.entries()) {
    if (!p.webPath) continue;
    const res = await fetch(p.webPath);
    const blob = await res.blob();
    const mime = blob.type || `image/${p.format || "jpeg"}`;
    files.push(new File([blob], `image-${Date.now()}-${i}.${extForMime(mime)}`, { type: mime }));
  }
  return files;
}

/**
 * Pick arbitrary documents/media from the device file system.
 * `accept` follows the HTML input `accept` syntax and is translated into
 * plugin mime types where possible.
 */
export async function pickDocuments(accept?: string, multiple = true): Promise<File[]> {
  if (!isNativeApp()) return [];
  const { FilePicker } = await import("@capawesome/capacitor-file-picker");

  const types = (accept || "")
    .split(",")
    .map((t) => t.trim())
    .filter((t) => t.includes("/"))
    .map((t) => t);

  const { files } = await FilePicker.pickFiles({
    types: types.length ? types : undefined,
    limit: multiple ? 0 : 1,
    readData: true,
  });

  const out: File[] = [];
  for (const f of files) {
    const mime = f.mimeType || "application/octet-stream";
    if (f.data) {
      out.push(await dataUrlToFile(`data:${mime};base64,${f.data}`, f.name, mime));
    } else if (f.blob) {
      out.push(new File([f.blob], f.name, { type: mime }));
    }
  }
  return out;
}

/** Route a pick request to the right native picker. */
export async function pickNativeFiles(
  source: NativePickSource,
  opts: { accept?: string; multiple?: boolean } = {},
): Promise<File[]> {
  const { accept, multiple = true } = opts;
  if (source === "camera") return captureCameraPhoto();
  if (source === "photos") return pickPhotos(multiple);
  return pickDocuments(accept, multiple);
}

/** True when the `accept` attribute only allows images. */
export const acceptIsImageOnly = (accept?: string | null): boolean => {
  if (!accept) return false;
  const parts = accept
    .split(",")
    .map((p) => p.trim().toLowerCase())
    .filter(Boolean);
  if (!parts.length) return false;
  return parts.every(
    (p) => p.startsWith("image/") || [".jpg", ".jpeg", ".png", ".webp", ".heic", ".gif"].includes(p),
  );
};
